/**
 * About values mosaic — flexible no-crop grid from image aspect ratios.
 * Same-ish AR → equal rows (landscape) or equal columns (portrait).
 * Mixed AR → largest tile + stacked companions, cell sizes matched to ARs.
 */
(function () {
  var SIMILAR_RATIO = 1.22;
  var MQ = '(min-width: 768px)';

  function aspectOf(card) {
    var img = card.querySelector('img.story-values-pillar-bg');
    if (img && img.naturalWidth > 0 && img.naturalHeight > 0) {
      return img.naturalWidth / img.naturalHeight;
    }
    var data = parseFloat(card.getAttribute('data-aspect-ratio') || '');
    return data > 0 ? data : 1.5;
  }

  function waitForImages(cards) {
    return Promise.all(
      cards.map(function (card) {
        var img = card.querySelector('img.story-values-pillar-bg');
        if (!img) return Promise.resolve();
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        return new Promise(function (resolve) {
          var done = function () {
            img.removeEventListener('load', done);
            img.removeEventListener('error', done);
            resolve();
          };
          img.addEventListener('load', done);
          img.addEventListener('error', done);
        });
      })
    );
  }

  function clearLayout(root, cards) {
    root.classList.remove(
      'story-values-mosaic--rows',
      'story-values-mosaic--columns',
      'story-values-mosaic--split'
    );
    root.style.removeProperty('--col-large');
    root.style.removeProperty('--col-stack');
    root.style.removeProperty('--row-a');
    root.style.removeProperty('--row-b');
    cards.forEach(function (card) {
      card.removeAttribute('data-mosaic-role');
      var data = card.getAttribute('data-aspect-ratio');
      if (data) {
        card.style.setProperty('--pillar-aspect-ratio', data);
      } else {
        card.style.removeProperty('--pillar-aspect-ratio');
      }
    });
  }

  function layout(root) {
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-pillar-card]'));
    if (!cards.length) return;

    clearLayout(root, cards);

    var ars = cards.map(aspectOf);
    cards.forEach(function (card, i) {
      card.style.setProperty('--pillar-aspect-ratio', String(ars[i]));
    });

    // Mobile: stacked natural-aspect tiles
    if (!window.matchMedia(MQ).matches || cards.length === 1) {
      root.classList.add('story-values-mosaic--rows');
      return;
    }

    var minAr = Math.min.apply(null, ars);
    var maxAr = Math.max.apply(null, ars);
    var similar = maxAr / minAr <= SIMILAR_RATIO;
    var allLandscape = ars.every(function (a) {
      return a >= 1;
    });

    if (similar) {
      root.classList.add(allLandscape ? 'story-values-mosaic--rows' : 'story-values-mosaic--columns');
      return;
    }

    if (cards.length === 2) {
      root.classList.add('story-values-mosaic--columns');
      return;
    }

    // Mixed ARs: largest image + two stacked companions (exact no-crop geometry)
    var largeIdx = 0;
    var bestScore = -1;
    cards.forEach(function (card, i) {
      var img = card.querySelector('img.story-values-pillar-bg');
      var score;
      if (img && img.naturalWidth && img.naturalHeight) {
        score = img.naturalWidth * img.naturalHeight;
      } else {
        // Prefer the image furthest from square when pixel area unknown
        score = Math.abs(Math.log(ars[i]));
      }
      if (score > bestScore) {
        bestScore = score;
        largeIdx = i;
      }
    });

    var others = [];
    for (var i = 0; i < cards.length; i++) {
      if (i !== largeIdx) others.push({ card: cards[i], ar: ars[i] });
    }
    // Only special-case first two companions for the mosaic; extras fall back to rows
    if (others.length < 2) {
      root.classList.add('story-values-mosaic--columns');
      return;
    }

    var arL = ars[largeIdx];
    var arA = others[0].ar;
    var arB = others[1].ar;

    var styles = window.getComputedStyle(root);
    var gap = parseFloat(styles.columnGap || styles.gap) || 0;
    var W = root.clientWidth;
    if (W <= 0) return;

    // Wl + gap + Wr = W
    // H = Wr/arA + Wr/arB + gap
    // Wl = arL * H  →  no crop when cover fills each cell
    var denom = 1 + arL / arA + arL / arB;
    var Wr = (W - gap * (1 + arL)) / denom;
    if (!(Wr > 1)) {
      root.classList.add('story-values-mosaic--rows');
      return;
    }

    var Ha = Wr / arA;
    var Hb = Wr / arB;
    var H = Ha + Hb + gap;
    var Wl = arL * H;

    root.classList.add('story-values-mosaic--split');
    root.style.setProperty('--col-large', Wl + 'px');
    root.style.setProperty('--col-stack', Wr + 'px');
    root.style.setProperty('--row-a', Ha + 'px');
    root.style.setProperty('--row-b', Hb + 'px');

    cards[largeIdx].setAttribute('data-mosaic-role', 'large');
    others[0].card.setAttribute('data-mosaic-role', 'stack-a');
    others[1].card.setAttribute('data-mosaic-role', 'stack-b');
  }

  function init(root) {
    if (!root || root.dataset.valuesMosaicReady === 'true') return;
    root.dataset.valuesMosaicReady = 'true';

    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-pillar-card]'));
    var scheduled = null;
    var run = function () {
      scheduled = null;
      layout(root);
    };
    var schedule = function () {
      if (scheduled) cancelAnimationFrame(scheduled);
      scheduled = requestAnimationFrame(run);
    };

    waitForImages(cards).then(schedule);

    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(schedule);
      ro.observe(root);
      root._valuesMosaicRo = ro;
    } else {
      window.addEventListener('resize', schedule);
    }

    if (window.matchMedia) {
      window.matchMedia(MQ).addEventListener('change', schedule);
    }
  }

  function boot(scope) {
    var root = scope || document;
    var nodes = root.querySelectorAll ? root.querySelectorAll('[data-story-values-mosaic]') : [];
    Array.prototype.forEach.call(nodes, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target) return;
    // Allow re-init inside replaced sections
    Array.prototype.forEach.call(
      event.target.querySelectorAll('[data-story-values-mosaic]'),
      function (node) {
        delete node.dataset.valuesMosaicReady;
      }
    );
    boot(event.target);
  });
})();
